import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { ThemeToggle } from '../../lib/ThemeToggle.js';
import { type CustomerMe, api } from '../../lib/api.js';
import { useSidebarCollapsed } from '../../lib/useSidebarCollapsed.js';

export function CustomerLayout() {
  const navigate = useNavigate();
  const { collapsed, toggle } = useSidebarCollapsed();
  const [me, setMe] = useState<CustomerMe | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<CustomerMe>('/api/customer/me')
      .then((data) => {
        setMe(data);
        setLoading(false);
      })
      .catch(() => {
        navigate('/customer/login', { replace: true });
      });
  }, [navigate]);

  async function logout() {
    await api.post('/api/customer/logout', {});
    navigate('/customer/login', { replace: true });
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-neutral-500">로딩…</div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {!collapsed && (
        <aside className="w-56 bg-surface text-neutral-900 border-r border-neutral-200 flex flex-col">
          <div className="flex items-center justify-between px-5 py-5 border-b border-neutral-200">
            <Link to="/customer" className="flex items-center gap-2 font-semibold text-neutral-900">
              <span className="w-6 h-6 rounded-md bg-brand-400 text-white grid place-items-center text-xs font-bold">
                ai
              </span>
              <span>aisight</span>
            </Link>
            <button
              type="button"
              onClick={toggle}
              title="사이드바 접기 (⌘\\)"
              aria-label="사이드바 접기"
              className="inline-flex items-center justify-center w-7 h-7 rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 transition-colors"
            >
              <CollapseIcon />
            </button>
          </div>
          <nav className="flex-1 px-3 py-4 space-y-1">
            <NavItem to="/customer/licenses">라이선스</NavItem>
            <NavItem to="/activate">활성화</NavItem>
            <NavItem to="/customer/inquiries">문의</NavItem>
            <NavItem to="/customer/account">내 정보</NavItem>
          </nav>
          <div className="px-5 py-4 border-t border-neutral-200 text-sm flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-neutral-500 text-xs truncate">{me?.email}</div>
              <button
                type="button"
                onClick={logout}
                className="mt-1 text-neutral-600 hover:text-brand-600 text-xs"
              >
                로그아웃
              </button>
            </div>
            <ThemeToggle className="shrink-0" />
          </div>
        </aside>
      )}
      <main className="flex-1 bg-neutral-50 min-w-0">
        {collapsed && (
          <div className="px-4 pt-4">
            <button
              type="button"
              onClick={toggle}
              title="사이드바 펼치기 (⌘\\)"
              aria-label="사이드바 펼치기"
              className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-surface border border-neutral-200 text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
            >
              <MenuIcon />
            </button>
          </div>
        )}
        <div className="max-w-6xl mx-auto px-8 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function NavItem({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `block px-3 py-2 rounded-md text-sm font-medium transition-colors ${
          isActive
            ? 'bg-brand-50 text-brand-600 dark:bg-brand-900 dark:text-brand-300'
            : 'text-neutral-700 hover:bg-neutral-100'
        }`
      }
    >
      {children}
    </NavLink>
  );
}

function CollapseIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect width="18" height="18" x="3" y="3" rx="2" />
      <path d="M9 3v18" />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="4" x2="20" y1="6" y2="6" />
      <line x1="4" x2="20" y1="12" y2="12" />
      <line x1="4" x2="20" y1="18" y2="18" />
    </svg>
  );
}
